import { Menu } from "@headlessui/react";
import clsx from "clsx";
import { forwardRef, type ComponentType, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import type { LucideProps } from "lucide-react";

type ActionIcon = ComponentType<LucideProps>;

const itemClass =
  "group flex min-h-10 w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-bold leading-tight text-primary transition hover:translate-x-0.5 hover:bg-secondary/10 hover:text-[#a32c07] focus:outline-none focus-visible:bg-secondary/10";

const dangerItemClass =
  "text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:bg-red-50";

const iconClass =
  "size-4 shrink-0 text-secondary transition group-hover:text-[#a32c07]";

const dangerIconClass = "text-red-500 group-hover:text-red-700";

export const dashboardActionMenuButtonClass =
  "inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-black/10 bg-white text-primary shadow-sm transition hover:border-secondary/30 hover:bg-secondary/10 hover:text-secondary hover:shadow-md focus:outline-none focus-visible:ring-4 focus-visible:ring-secondary/15";

export const dialogOverlayClass =
  "fixed inset-0 z-10 w-screen overflow-y-auto bg-primary/35 backdrop-blur-sm";

export const dialogCenterClass =
  "flex min-h-full items-center justify-center p-4";

export const dialogPanelClass =
  "relative z-[9999999999999] w-full max-w-md rounded-2xl border border-black/10 bg-white p-6 text-textPrimary shadow-[0_30px_90px_rgba(17,24,22,0.24)]";

export const dialogTitleClass =
  "text-center text-lg font-extrabold text-primary";

export const dialogTextClass = "mt-2 mb-4 text-center text-sm text-lightGray";

export const primaryDialogButtonClass =
  "inline-flex min-h-11 items-center justify-center rounded-full bg-secondary px-6 py-2 text-sm font-bold text-white shadow-[0_14px_30px_rgba(254,87,34,0.2)] transition hover:bg-[#a32c07] focus:outline-none focus-visible:ring-4 focus-visible:ring-secondary/20 disabled:cursor-not-allowed disabled:opacity-60";

export const subtleDialogButtonClass =
  "inline-flex min-h-11 items-center justify-center rounded-full border border-black/10 bg-white px-6 py-2 text-sm font-bold text-textPrimary transition hover:bg-dashboardBg focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/10";

export const dangerDialogButtonClass =
  "inline-flex min-h-11 items-center justify-center rounded-full bg-red-600 px-6 py-2 text-sm font-bold text-white transition hover:bg-red-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-100";

type DashboardActionMenuPanelProps = {
  children: ReactNode;
  className?: string;
};

export const DashboardActionMenuPanel = forwardRef<
  HTMLDivElement,
  DashboardActionMenuPanelProps
>(({ children, className }, ref) => (
  <Menu.Items
    ref={ref}
    className={clsx(
      "absolute bottom-[calc(100%+0.75rem)] right-0 z-[90] max-h-[min(70vh,34rem)] w-[min(18rem,calc(100vw-2rem))] min-w-60 overflow-y-auto rounded-2xl border border-black/10 bg-white/95 p-2 text-left shadow-[0_24px_70px_rgba(17,24,22,0.18)] backdrop-blur-xl focus:outline-none",
      className
    )}
  >
    <div className="grid gap-1">{children}</div>
    <span className="pointer-events-none absolute -bottom-1.5 right-5 h-3 w-3 rotate-45 border-b border-r border-black/10 bg-white" />
  </Menu.Items>
));

DashboardActionMenuPanel.displayName = "DashboardActionMenuPanel";

export const DashboardMenuLink = ({
  children,
  icon: Icon,
  danger = false,
  className,
  ...props
}: LinkProps & {
  children: ReactNode;
  icon: ActionIcon;
  danger?: boolean;
}) => (
  <Menu.Item>
    <Link
      {...props}
      className={clsx(itemClass, danger && dangerItemClass, className)}
    >
      <Icon className={clsx(iconClass, danger && dangerIconClass)} />
      <span>{children}</span>
    </Link>
  </Menu.Item>
);

export const DashboardMenuButton = ({
  children,
  icon: Icon,
  danger = false,
  className,
  type = "button",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  icon: ActionIcon;
  danger?: boolean;
}) => (
  <Menu.Item>
    <button
      {...props}
      type={type}
      className={clsx(itemClass, danger && dangerItemClass, className)}
    >
      <Icon className={clsx(iconClass, danger && dangerIconClass)} />
      <span>{children}</span>
    </button>
  </Menu.Item>
);
